import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { filter, finalize } from 'rxjs/operators';
import { CurrentContextService } from '../../../core/context/current-context.service';
import {
  LiveUpdateEventTypes,
  growthSearchOpsTopic,
  liveStatusTranslationKey
} from '../../../core/live-updates/live-update.models';
import { LiveUpdateService } from '../../../core/live-updates/live-update.service';
import { LocalizationService } from '../../../core/localization/localization.service';
import { AinFormFieldComponent } from '../../../shared/design-system/form-field.component';
import { InfoHintComponent } from '../../../shared/design-system/info-hint.component';
import { AinPageComponent } from '../../../shared/design-system/page.component';
import { AinStateComponent } from '../../../shared/design-system/state.component';
import { StructuredJsonDisplayComponent } from '../../../shared/design-system/structured-json-display.component';
import { StructuredJsonFormComponent } from '../../../shared/design-system/structured-json-form.component';
import {
  SearchApplyJobDto,
  SearchPropertyListItemDto,
  SearchUrlInventoryDto,
  searchOpsPatchFields
} from './growth-search-ops.models';
import { GrowthSearchOpsService } from './growth-search-ops.service';

@Component({
  standalone: true,
  selector: 'ain-growth-search-ops-apply-queue',
  imports: [
    AinPageComponent,
    AinStateComponent,
    InfoHintComponent,
    AinFormFieldComponent,
    FormsModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    TableModule,
    RouterLink,
    StructuredJsonFormComponent,
    StructuredJsonDisplayComponent
  ],
  template: `
    <ain-page
      [eyebrow]="t('growth.eyebrow')"
      [title]="t('growth.searchops.applyQueue.title')"
      [description]="t('growth.searchops.applyQueue.description')">
      <ain-info-hint
        page-title-addon
        [titleKey]="'growth.searchops.applyQueue.help.title'"
        [descriptionKey]="'growth.searchops.applyQueue.help.description'"
        [bulletKeys]="[
          'growth.searchops.applyQueue.help.bullets.draft',
          'growth.searchops.applyQueue.help.bullets.approve',
          'growth.searchops.applyQueue.help.bullets.storefront',
          'growth.searchops.applyQueue.help.bullets.externalAck',
          'growth.searchops.applyQueue.help.bullets.plpGap'
        ]" />

      <div class="toolbar">
        <button pButton type="button" severity="secondary" [label]="t('common.refresh')" (click)="load(true)"></button>
        <a pButton severity="secondary" [routerLink]="['/growth/search-ops/urls']" [queryParams]="propertyQuery()" [label]="t('growth.searchops.workHome.actions.urls')"></a>
        <span class="live-badge">{{ t(liveStatusKey(liveUpdateService.connectionState())) }}</span>
      </div>

      @if (loading()) {
        <ain-state type="loading" [title]="t('common.loading')" />
      } @else if (error()) {
        <ain-state type="error" [title]="t('common.error')" [message]="error()" />
      } @else if (properties().length === 0) {
        <ain-state type="empty" [title]="t('growth.searchops.workHome.empty.title')" [message]="t('growth.searchops.workHome.empty.message')" />
      } @else {
        <section class="card">
          <ain-form-field [label]="t('growth.searchops.fields.property')">
            <p-select
              [options]="propertyOptions()"
              optionLabel="label"
              optionValue="value"
              [(ngModel)]="selectedPropertyId"
              appendTo="body"
              (onChange)="onPropertyChange($event.value)" />
          </ain-form-field>
        </section>

        <section class="card">
          <div class="section-title">
            <h3>{{ t('growth.searchops.applyQueue.sections.queue') }}</h3>
            <ain-info-hint
              [titleKey]="'growth.searchops.applyQueue.queueHelp.title'"
              [descriptionKey]="'growth.searchops.applyQueue.queueHelp.description'" />
          </div>
          <div class="toolbar">
            <button pButton type="button" [label]="t('growth.searchops.applyQueue.actions.draft')" (click)="openDraft()"></button>
          </div>
          <p-table [value]="jobs()" [paginator]="true" [rows]="20" dataKey="id" [(selection)]="selectedJob" selectionMode="single">
            <ng-template pTemplate="header">
              <tr>
                <th>{{ t('growth.searchops.fields.url') }}</th>
                <th>{{ t('growth.searchops.fields.status') }}</th>
                <th>{{ t('growth.searchops.fields.dryRun') }}</th>
                <th>{{ t('growth.searchops.fields.actions') }}</th>
              </tr>
            </ng-template>
            <ng-template pTemplate="body" let-row>
              <tr [pSelectableRow]="row">
                <td class="wrap-url">{{ urlLabel(row.urlInventoryId) }}</td>
                <td>{{ t('growth.searchops.applyStatus.' + row.status) }}</td>
                <td>{{ row.dryRun ? t('common.yes') : t('common.no') }}</td>
                <td class="actions">
                  @if (row.status === 'draft' && canApply()) {
                    <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.applyQueue.actions.approve')" (click)="approve(row)"></button>
                  }
                  @if (canSendToStorefront(row)) {
                    <button pButton type="button" size="small" [disabled]="busy()" [label]="t('growth.searchops.applyQueue.actions.applyStorefront')" (click)="sendToStorefront(row)"></button>
                  }
                </td>
              </tr>
            </ng-template>
            <ng-template pTemplate="emptymessage">
              <tr>
                <td colspan="4">{{ t('growth.searchops.applyQueue.empty') }}</td>
              </tr>
            </ng-template>
          </p-table>
        </section>

        @if (selectedJob; as job) {
          <section class="card">
            <div class="section-title">
              <h3>{{ t('growth.searchops.applyQueue.sections.patch') }}</h3>
              <ain-info-hint
                [titleKey]="'growth.searchops.applyQueue.patchHelp.title'"
                [descriptionKey]="'growth.searchops.applyQueue.patchHelp.description'" />
            </div>
            <ain-structured-json-display [fields]="patchFields" [payloadJson]="job.patchJson" />
            <div class="toolbar">
              @if (urlRecord(job.urlInventoryId)?.seoProfileId; as seoProfileId) {
                <a pButton severity="secondary" size="small" [routerLink]="['/growth/seo/profiles', seoProfileId]" [label]="t('growth.searchops.applyQueue.links.seoProfile')"></a>
              }
              @if (urlRecord(job.urlInventoryId)?.contentAssetId; as contentAssetId) {
                <a pButton severity="secondary" size="small" [routerLink]="['/growth/marketing/article-publishing', contentAssetId]" [label]="t('growth.searchops.applyQueue.links.article')"></a>
              }
              @if (urlRecord(job.urlInventoryId)?.offeringId) {
                <a pButton severity="secondary" size="small" [routerLink]="['/marketplace/storefront/publishing']" [queryParams]="{ q: urlLabel(job.urlInventoryId) }" [label]="t('growth.searchops.applyQueue.links.storefront')"></a>
              }
            </div>
          </section>
        }
      }
    </ain-page>

    <p-dialog
      [header]="t('growth.searchops.applyQueue.dialogs.draft')"
      [(visible)]="draftOpen"
      [modal]="true"
      [style]="{ width: '36rem' }"
      appendTo="body">
      <ain-info-hint
        [titleKey]="'growth.searchops.applyQueue.draftHelp.title'"
        [descriptionKey]="'growth.searchops.applyQueue.draftHelp.description'" />
      <ain-form-field [label]="t('growth.searchops.fields.url')">
        <p-select [options]="urlOptions()" optionLabel="label" optionValue="value" [(ngModel)]="draftUrlId" appendTo="body" />
      </ain-form-field>
      <ain-structured-json-form [fields]="patchFields" [(payloadJson)]="draftPatchJson" />
      <div class="toolbar">
        <button pButton type="button" [disabled]="busy()" [label]="t('common.save')" (click)="saveDraft()"></button>
      </div>
    </p-dialog>
  `,
  styles: [`
    .toolbar { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; margin-bottom: 12px; }
    .card { background: var(--ain-color-surface, #fff); padding: 16px; border-radius: 12px; margin-bottom: 12px; }
    .section-title { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }
    .section-title h3 { margin: 0; font-size: 1rem; }
    .live-badge { font-size: 0.85rem; color: #64748b; }
    .actions { display: flex; flex-wrap: wrap; gap: 6px; }
    .wrap-url { overflow-wrap: anywhere; }
  `]
})
export class GrowthSearchOpsApplyQueueComponent implements OnInit {
  private readonly api = inject(GrowthSearchOpsService);
  private readonly localization = inject(LocalizationService);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly context = inject(CurrentContextService);
  private readonly destroyRef = inject(DestroyRef);
  readonly liveUpdateService = inject(LiveUpdateService);
  readonly liveStatusKey = liveStatusTranslationKey;
  readonly patchFields = searchOpsPatchFields;

  readonly loading = signal(false);
  readonly busy = signal(false);
  readonly error = signal('');
  readonly properties = signal<SearchPropertyListItemDto[]>([]);
  readonly jobs = signal<SearchApplyJobDto[]>([]);
  readonly urls = signal<SearchUrlInventoryDto[]>([]);
  selectedPropertyId: string | null = null;
  selectedJob: SearchApplyJobDto | null = null;
  draftOpen = false;
  draftUrlId: string | null = null;
  draftPatchJson = '{}';
  private subscribedTopic: string | null = null;

  ngOnInit(): void {
    this.liveUpdateService.events$
      .pipe(filter(e => e.eventType === LiveUpdateEventTypes.growthSearchOpsChanged), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => this.load(false));
    this.destroyRef.onDestroy(() => {
      if (this.subscribedTopic) {
        void this.liveUpdateService.unsubscribe(this.subscribedTopic);
        this.subscribedTopic = null;
      }
    });
    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(params => {
      this.selectedPropertyId = params.get('propertyId');
      this.load(true);
    });
    void this.setupLiveSubscription();
  }

  t(key: string): string {
    return this.localization.translate(key);
  }

  propertyOptions() {
    return this.properties().map(item => ({ value: item.id, label: `${item.name} (${item.primaryDomain})` }));
  }

  urlOptions() {
    return this.urls().map(item => ({ value: item.id, label: item.url }));
  }

  urlLabel(urlId: string | null): string {
    if (!urlId) {
      return '—';
    }
    return this.urls().find(item => item.id === urlId)?.url ?? '—';
  }

  urlRecord(urlId: string | null): SearchUrlInventoryDto | null {
    if (!urlId) {
      return null;
    }
    return this.urls().find(item => item.id === urlId) ?? null;
  }

  canApply(): boolean {
    const ctx = this.context.context();
    if (ctx?.roles?.includes('SystemAdmin') || ctx?.isPlatformAdmin) {
      return true;
    }
    return (ctx?.permissions ?? []).some(item => item.key === 'growth.searchops.apply');
  }

  isStorefrontProperty(): boolean {
    return this.selectedProperty()?.siteKind === 'ain_storefront';
  }

  selectedProperty(): SearchPropertyListItemDto | null {
    if (!this.selectedPropertyId) {
      return null;
    }
    return this.properties().find(item => item.id === this.selectedPropertyId) ?? null;
  }

  canSendToStorefront(row: SearchApplyJobDto): boolean {
    return this.canApply()
      && this.isStorefrontProperty()
      && (row.status === 'awaiting_approval' || row.status === 'sent' || row.status === 'failed');
  }

  propertyQuery() {
    return this.selectedPropertyId ? { propertyId: this.selectedPropertyId } : {};
  }

  onPropertyChange(propertyId: string): void {
    void this.router.navigate([], { relativeTo: this.route, queryParams: { propertyId }, queryParamsHandling: 'merge' });
  }

  load(showSpinner: boolean): void {
    if (showSpinner) {
      this.loading.set(true);
    }
    this.error.set('');
    this.api.listProperties().pipe(finalize(() => this.loading.set(false))).subscribe({
      next: properties => {
        this.properties.set(properties);
        if (!this.selectedPropertyId && properties.length > 0) {
          this.selectedPropertyId = properties[0].id;
        }
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  openDraft(): void {
    this.draftUrlId = this.urls()[0]?.id ?? null;
    this.draftPatchJson = '{}';
    this.draftOpen = true;
  }

  saveDraft(): void {
    if (!this.selectedPropertyId) {
      return;
    }
    this.busy.set(true);
    this.api.createApplyJob({
      searchPropertyId: this.selectedPropertyId,
      urlInventoryId: this.draftUrlId,
      patchJson: this.draftPatchJson
    }).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => {
        this.draftOpen = false;
        this.loadBoard();
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.saveFailed'))
    });
  }

  approve(row: SearchApplyJobDto): void {
    this.busy.set(true);
    this.api.approveApplyJob(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.loadBoard(),
      error: err => this.error.set(this.errorKey(err?.error?.error))
    });
  }

  sendToStorefront(row: SearchApplyJobDto): void {
    if (!this.canSendToStorefront(row)) {
      return;
    }
    this.busy.set(true);
    this.api.sendApplyJob(row.id).pipe(finalize(() => this.busy.set(false))).subscribe({
      next: () => this.loadBoard(),
      error: err => this.error.set(this.errorKey(err?.error?.error))
    });
  }

  private errorKey(code: string | undefined): string {
    if (!code) {
      return this.t('growth.searchops.messages.saveFailed');
    }
    if (code.startsWith('growth_searchops_') || code.startsWith('growth_storefront_')) {
      return this.t('growth.searchops.errors.' + code);
    }
    return code;
  }

  private loadBoard(): void {
    if (!this.selectedPropertyId) {
      this.jobs.set([]);
      this.urls.set([]);
      return;
    }
    this.api.listUrls(this.selectedPropertyId).subscribe({
      next: urls => this.urls.set(urls)
    });
    this.api.listApplyJobs(this.selectedPropertyId).subscribe({
      next: jobs => {
        this.jobs.set(jobs);
        if (this.selectedJob) {
          this.selectedJob = jobs.find(item => item.id === this.selectedJob?.id) ?? null;
        }
      },
      error: err => this.error.set(err?.error?.error ?? this.t('growth.searchops.messages.loadFailed'))
    });
  }

  private async setupLiveSubscription(): Promise<void> {
    const tenantId = this.context.context()?.tenantId;
    if (!tenantId) {
      return;
    }
    this.subscribedTopic = growthSearchOpsTopic(tenantId);
    await this.liveUpdateService.subscribe(this.subscribedTopic);
  }
}
